import React from 'react'
import { elPt9, elRowGap4, elW3, FlexContainer, Icon, InputGroup, Loader, ModalBg } from '@reapit/elements'
import { useSearchParams } from 'react-router-dom'
import { css, cx } from '@linaria/core'
import { TTableRowAction } from '@reapitform/common-ui'
import { AgreementListItem } from 'apps/agent/src/core/api/types/agreements'
import { ActivityListModal } from '../../../../components/modals'
import { AgreementTable } from '../agreement-table'
import { useAgreementPdf } from '../../../../api/agreements/get-agreement-pdf'

export const AgreementCloseTab: React.FC = React.memo(() => {
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedAgreementId, setSelectedAgreementId] = React.useState<string[] | undefined>()
  const [selectedAgreement, setSelectedAgreement] = React.useState<AgreementListItem>()
  const [isOpenActivityModal, setIsOpenActivityModal] = React.useState<boolean>(false)
  const { data, loading } = useAgreementPdf(isDownloading ? selectedAgreementId?.[0] : undefined)

  const handleSearch = (event?: React.ChangeEvent<HTMLInputElement>) => {
    React.startTransition(() => {
      setSearchParams((prevParams) => {
        if (event?.target.value) prevParams.set('query', event?.target.value)
        else prevParams.delete('query')

        return prevParams
      })
    })
  }

  const openPdf = (pdfUrl: string) => {
    const anchorElement: HTMLAnchorElement = document.createElement('a')
    anchorElement.href = pdfUrl
    anchorElement.target = '_blank'
    anchorElement.rel = 'noopener noreferrer'
    anchorElement.style.display = 'none'
    document.body.appendChild(anchorElement)

    anchorElement.click()
    document.body.removeChild(anchorElement)
  }

  React.useEffect(() => {
    if (loading) return
    if (data) openPdf(data)
    setIsDownloading(false)
  }, [data, loading])

  const rowActions: TTableRowAction<AgreementListItem>[] = React.useMemo(
    () => [
      {
        actionTitle: 'Download',
        actionMultipleTitle: <Icon icon="trash" intent="low" iconSize="smallest" fontSize="inherit !important" />,
        onClick: (data) => {
          setSelectedAgreementId([data.referenceID])
          setIsDownloading(true)
        },
      },
      {
        actionTitle: 'View Activity',
        onClick: (data) => {
          setSelectedAgreement(data)
          setIsOpenActivityModal(true)
        },
        disabledCombinedAction: true,
      },
    ],
    [],
  )

  return (
    <FlexContainer tag="section" isFlexColumn className={cx(elRowGap4, elPt9)}>
      <InputGroup
        type="text"
        icon="search"
        onChange={handleSearch}
        className={cx(
          elW3,
          css`
            .el-input {
              height: 2rem !important;
            }
          `,
        )}
        placeholder="Search"
        defaultValue={searchParams.get('query') ?? ''}
      />
      <AgreementTable rowActions={rowActions} disableSelection defaultStatus="Closed" />
      {selectedAgreement && (
        <ActivityListModal
          isOpen={isOpenActivityModal}
          documentId={selectedAgreement?.referenceID}
          isOpenChange={(isOpen) => setIsOpenActivityModal(isOpen)}
          agreementName={selectedAgreement.formName}
        />
      )}
      {isDownloading && (
        <>
          <ModalBg className="el-is-active" />
          <Loader fullPage label="downloading ..." />
        </>
      )}
    </FlexContainer>
  )
})
